import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { LocalDate } from "@js-joda/core";

import useApi from "../../../../shared/hooks/useApi";
import {
  PatientTaskFuture__OrderField,
  PatientTaskRegular__OrderField,
  SortDir,
} from "../../../../shared/schema/gql/graphql";
import {
  GET_PATIENT_TASK_FUTURES_QUERY,
  GET_PATIENT_TASK_REGULARS_QUERY,
} from "../tasks-new.graphql";
import { useTasksNewFilters } from "./useTasksNewFilters";

// ---------------------------------------------------------------------------
// Result row types
// We derive row types from the inferred return type of each query. Because the
// graphql() function returns `unknown` for queries not yet in the code-gen map
// we rely on the generated GQL schema types (PatientTaskRegular / PatientTaskFuture)
// rather than using ReturnType magic, which would resolve to `unknown`.
// ---------------------------------------------------------------------------

import type {
  PatientTaskFuture,
  PatientTaskRegular,
  PatientTaskRegularDocument,
} from "../../../../shared/schema/gql/graphql";

export type RegularRow = PatientTaskRegular & { __kind: "regular" };
export type FutureRow = PatientTaskFuture & {
  __kind: "future";
  documents: { nodes: PatientTaskRegularDocument[] };
};
export type MergedRow = RegularRow | FutureRow;

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useTasksNewData(): {
  rows: MergedRow[];
  totalCount: number;
  isPending: boolean;
  isFetching: boolean;
  isError: boolean;
  refetch: () => void;
} {
  const { api } = useApi();
  const filters = useTasksNewFilters();

  const page = filters.getValue("page") ?? 1;
  const pageSize = filters.getValue("pageSize") ?? 25;
  const scheduledFrom = filters.getValue("scheduledFrom") ?? null;
  const scheduledTo = filters.getValue("scheduledTo") ?? null;
  const statuses = filters.getValue("statuses") ?? [];
  const taskTemplateIds = filters.getValue("taskTemplateIds") ?? [];
  const caregiverIds = filters.getValue("caregiverIds") ?? [];
  const patientIds = filters.getValue("patientIds") ?? [];
  const contractTypeIds = filters.getValue("contractTypeIds") ?? [];
  const officeIds = filters.getValue("officeIds") ?? [];
  const sortDirection = filters.getValue("sortDirection") ?? "DESC";

  // ---------------------------------------------------------------------------
  // Pagination approximation (v1)
  //
  // Because we have two independent paginated queries, true cross-query pagination
  // would require fetching totals first then distributing page slots. Instead we
  // take the simpler approach: fetch (pageSize / 2) rows from each side and use
  // offset = (page - 1) * (pageSize / 2). This means a page will always contain
  // half its rows from regulars and half from futures. If the two collections are
  // very uneven (e.g., 1000 regulars vs 10 futures) the page will appear skewed —
  // the futures side will exhaust early while regulars continue. Accuracy improves
  // in a v2 by fetching a larger window per side and slicing client-side.
  // ---------------------------------------------------------------------------
  const halfSize = Math.max(1, Math.round(pageSize / 2));
  const offset = (page - 1) * halfSize;

  const sortDir: SortDir = sortDirection === "ASC" ? SortDir.Asc : SortDir.Desc;

  const regularOrder = [{ field: PatientTaskRegular__OrderField.ScheduledDate, sort: sortDir }];
  const futureOrder = [{ field: PatientTaskFuture__OrderField.ScheduledDate, sort: sortDir }];

  const regularVariables = {
    limit: halfSize,
    offset,
    order: regularOrder,
    scheduledFrom: scheduledFrom as LocalDate | null,
    scheduledTo: scheduledTo as LocalDate | null,
    statuses: statuses.length > 0 ? statuses : null,
    taskTemplateIds: taskTemplateIds.length > 0 ? taskTemplateIds : null,
    caregiverIds: caregiverIds.length > 0 ? caregiverIds : null,
    patientIds: patientIds.length > 0 ? patientIds : null,
    contractTypeIds: contractTypeIds.length > 0 ? contractTypeIds : null,
    officeIds: officeIds.length > 0 ? officeIds : null,
  };

  const futureVariables = {
    limit: halfSize,
    offset,
    order: futureOrder,
    scheduledFrom: scheduledFrom as LocalDate | null,
    scheduledTo: scheduledTo as LocalDate | null,
    taskTemplateIds: taskTemplateIds.length > 0 ? taskTemplateIds : null,
    caregiverIds: caregiverIds.length > 0 ? caregiverIds : null,
    patientIds: patientIds.length > 0 ? patientIds : null,
    contractTypeIds: contractTypeIds.length > 0 ? contractTypeIds : null,
  };

  // Fire both queries in parallel. React Query deduplicates identical queryKeys.
  const regularsQuery = useQuery({
    placeholderData: keepPreviousData,
    queryKey: ["patientTaskRegulars", regularVariables],
    queryFn: () =>
      api.graphql(GET_PATIENT_TASK_REGULARS_QUERY as any, { variables: regularVariables as any }),
  });

  const futuresQuery = useQuery({
    placeholderData: keepPreviousData,
    queryKey: ["patientTaskFutures", futureVariables],
    queryFn: () =>
      api.graphql(GET_PATIENT_TASK_FUTURES_QUERY as any, { variables: futureVariables as any }),
  });

  const isPending = regularsQuery.isPending || futuresQuery.isPending;
  const isFetching = regularsQuery.isFetching || futuresQuery.isFetching;
  const isError = regularsQuery.isError || futuresQuery.isError;

  // ---------------------------------------------------------------------------
  // Merge + sort
  // ---------------------------------------------------------------------------
  const regularNodes: RegularRow[] = (
    (regularsQuery.data as any)?.patientTaskRegulars?.nodes ?? []
  ).map((n: PatientTaskRegular) => ({ ...n, __kind: "regular" as const }));

  const futureNodes: FutureRow[] = (
    (futuresQuery.data as any)?.patientTaskFutures?.nodes ?? []
  ).map((n: PatientTaskFuture) => ({
    documents: { nodes: [] },
    ...n,
    __kind: "future" as const,
  }));

  const merged: MergedRow[] = [...regularNodes, ...futureNodes];

  // Re-sort merged array client-side so rendering order is stable and consistent
  // with the user-selected direction regardless of which side returned first.
  merged.sort((a, b) => {
    const da = a.scheduledDate;
    const db = b.scheduledDate;
    if (da == null && db == null) return 0;
    if (da == null) return 1;
    if (db == null) return -1;
    const cmp = da.compareTo(db);
    return sortDirection === "ASC" ? cmp : -cmp;
  });

  const regularsTotal: number = (regularsQuery.data as any)?.patientTaskRegulars?.totalCount ?? 0;
  const futuresTotal: number = (futuresQuery.data as any)?.patientTaskFutures?.totalCount ?? 0;
  const totalCount = regularsTotal + futuresTotal;

  function refetch() {
    regularsQuery.refetch();
    futuresQuery.refetch();
  }

  return { rows: merged, totalCount, isPending, isFetching, isError, refetch };
}
